/**
 * OnboardingRedirector — top-level guard.
 *
 * Mount this once near the App root, BELOW the workspace + auth providers.
 * On every route change it asks /api/onboarding/state; if the workspace
 * isn't onboarded AND has no flows/connections/event-designs, it pushes
 * the user to /onboarding/welcome.
 *
 * Bypass:
 *   - `?skip-onboarding` query string (records the skip in audit log)
 *   - User is already on `/onboarding/*`, `/welcome/*`, `/settings/*`,
 *     or any auth/SSO landing page
 *
 * The redirector never renders UI — it only side-effects via navigate().
 * Children are returned untouched.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboarding } from '../hooks/useOnboarding';
import { api } from '../api';

const BYPASS_PREFIXES = [
  '/onboarding',
  '/welcome',
  '/settings',
  '/auth',
];

function shouldBypass(pathname: string): boolean {
  return BYPASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export function OnboardingRedirector({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, loading } = useOnboarding();
  const skipRecorded = useRef(false);

  useEffect(() => {
    if (loading || !state) return;

    // ?skip-onboarding query — record + clear the param.
    const search = new URLSearchParams(location.search);
    if (search.has('skip-onboarding') && !skipRecorded.current) {
      skipRecorded.current = true;
      // Fire-and-forget — this writes to audit + flips workspace flag.
      void api.onboardingSkip().catch(() => undefined);
      // Strip the param so we don't re-trigger on every render.
      search.delete('skip-onboarding');
      const next = location.pathname + (search.toString() ? `?${search.toString()}` : '');
      navigate(next, { replace: true });
      return;
    }

    if (state.needsOnboarding && !shouldBypass(location.pathname)) {
      navigate('/onboarding/welcome', { replace: true });
    }
  }, [loading, state, location.pathname, location.search, navigate]);

  return <>{children}</>;
}
